import React from 'react';

const RING_SIZE = {
  sm: 'h-4 w-4 border-2',
  md: 'h-8 w-8 border-2',
  lg: 'h-9 w-9 border-2',
  xl: 'h-12 w-12 border-2',
};

/**
 * App-wide loading ring. Uses CSS variables in the dashboard; optional tones for marketing / dark buttons.
 * Spinning is disabled when the user prefers reduced motion (solid dot instead).
 */
export function LoadingSpinnerRing({ size = 'md', tone = 'token', className = '' }) {
  const toneClass =
    tone === 'marketing'
      ? 'border-white/20 border-t-[#FF56A5] motion-reduce:border-transparent motion-reduce:bg-[#FF56A5]'
      : tone === 'onDark'
        ? 'border-white/30 border-t-white motion-reduce:border-transparent motion-reduce:bg-white'
        : 'border-[var(--app-border,rgba(148,163,184,0.45))] border-t-[var(--app-accent,#0f766e)] motion-reduce:border-transparent motion-reduce:bg-[var(--app-accent,#0f766e)]';

  return (
    <div
      className={`animate-spin rounded-full motion-reduce:animate-none ${toneClass} ${RING_SIZE[size] || RING_SIZE.md} ${className}`.trim()}
      aria-hidden="true"
    />
  );
}

const LAYOUT_WRAPPER = {
  page: 'flex min-h-screen w-full items-center justify-center',
  section: 'flex min-h-[min(60vh,28rem)] w-full items-center justify-center',
  compact: 'flex min-h-[12rem] w-full items-center justify-center py-8',
  embed: 'flex min-h-[16rem] w-full items-center justify-center',
  inline: 'inline-flex flex-row items-center justify-center gap-2',
};

const DEFAULT_SPINNER_SIZE = {
  page: 'xl',
  section: 'lg',
  compact: 'md',
  embed: 'md',
  inline: 'sm',
};

/**
 * Consistent full-page, section, modal, overlay, and inline loading UI.
 *
 * @param {'page'|'section'|'compact'|'embed'|'inline'} layout
 * @param {boolean} overlay — absolute inset-0 + veil (parent must be position:relative)
 * @param {boolean} blocking — fixed inset-0 (fullscreen blocker)
 * @param {'light'|'dim'} blockingTint — fullscreen backdrop
 * @param {boolean} withCard — panel card (e.g. long operations)
 */
export default function AppLoadingState({
  layout = 'section',
  overlay = false,
  blocking = false,
  blockingTint = 'light',
  withCard = false,
  label = 'Loading…',
  description,
  showLabel = true,
  tone = 'token',
  spinnerSize,
  className = '',
  cardClassName = '',
}) {
  const ringSize = spinnerSize || DEFAULT_SPINNER_SIZE[layout] || 'md';
  const ariaLabel = (showLabel && label) ? label : 'Loading';

  const textBlock =
    showLabel && (label || description) ? (
      layout === 'inline' ? (
        label ? (
          <span className="text-sm text-slate-600 dark:text-slate-400">{label}</span>
        ) : null
      ) : (
        <div className="mt-4 max-w-sm px-4 text-center">
          {label ? (
            <p className="text-sm font-medium text-slate-600 dark:text-slate-300">{label}</p>
          ) : null}
          {description ? (
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{description}</p>
          ) : null}
        </div>
      )
    ) : null;

  const body = (
    <div
      className={
        layout === 'inline'
          ? 'contents'
          : 'flex flex-col items-center justify-center'
      }
    >
      <LoadingSpinnerRing size={ringSize} tone={tone} />
      {textBlock}
    </div>
  );

  const regionProps = {
    role: 'status',
    'aria-live': 'polite',
    'aria-busy': 'true',
    'aria-label': ariaLabel,
  };

  if (overlay) {
    return (
      <div
        className={`absolute inset-0 z-10 flex items-center justify-center bg-white/80 backdrop-blur-[2px] dark:bg-slate-950/75 ${className}`.trim()}
        {...regionProps}
      >
        {body}
      </div>
    );
  }

  if (blocking) {
    const backdrop =
      blockingTint === 'dim'
        ? 'bg-black/50 dark:bg-black/60'
        : 'bg-white dark:bg-slate-950';

    if (withCard) {
      return (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${backdrop} ${className}`.trim()}
          {...regionProps}
        >
          <div
            className={`w-full max-w-md rounded-xl border border-[var(--app-border)] bg-[var(--app-surface-strong)] p-8 shadow-xl dark:border-slate-600 dark:bg-slate-900 ${cardClassName}`.trim()}
          >
            {body}
          </div>
        </div>
      );
    }

    return (
      <div
        className={`fixed inset-0 z-50 flex items-center justify-center ${backdrop} ${className}`.trim()}
        {...regionProps}
      >
        {body}
      </div>
    );
  }

  return (
    <div
      className={`${LAYOUT_WRAPPER[layout] || LAYOUT_WRAPPER.section} ${className}`.trim()}
      {...regionProps}
    >
      {body}
    </div>
  );
}
